import React, { useMemo, useState, useCallback } from 'react';
import { GameState, Player, DisplayFixture, Cup, League } from '../../../domain/entities';
import { FixturesHelper } from '../../../services/FixturesHelper';
import { View } from '../../../App';
import { NextMatchPanel } from './NextMatchPanel';
import { LastResultPanel } from './LastResultPanel';
import { TeamStatusPanel } from './TeamStatusPanel';
import { TeamOverviewPanel } from './TeamOverviewPanel';
import { WeeklyPunditPanel } from './WeeklyPunditPanel';
import { MatchDetailsModal } from '../../shared/MatchDetailsModal';
import { PlayerConversationModal } from '../../shared/PlayerConversationModal';
import { TabProps } from '../DashboardScreen';

export const OverviewTab: React.FC<TabProps> = (props) => {
    const { gameState, pushView, onPlayerConversation, onOfferFinancialIncentive, isAiPunditEnabled, showRatingsOnMatchReport, onCacheAiMatchReport } = props;
    const [selectedFixture, setSelectedFixture] = useState<DisplayFixture | null>(null);
    const [playerToTalkTo, setPlayerToTalkTo] = useState<Player | null>(null);

    const allPlayerFixtures = useMemo(() => {
        const leagueFixtures: DisplayFixture[] = FixturesHelper.getTeamFixturesInCompetition(gameState.fixtures, gameState.playerTeamName, gameState.playerLeagueName, true)
            .map(f => ({ ...f, competition: gameState.playerLeagueName, type: 'league' }));

        const cupFixtures: DisplayFixture[] = Object.values(gameState.cups)
            .flatMap((cup: Cup) =>
                FixturesHelper.getTeamFixturesInCompetition(gameState.fixtures, gameState.playerTeamName, cup.name, false)
                    .map(f => ({ ...f, competition: cup.name, type: 'cup' }))
            );

        return [...leagueFixtures, ...cupFixtures].sort((a, b) => a.week - b.week);
    }, [gameState]);

    const nextFixture = useMemo(() => allPlayerFixtures.find(f => f.week >= gameState.currentWeek && !f.result), [gameState.currentWeek, allPlayerFixtures]);
    const lastFixture = useMemo(() => [...allPlayerFixtures].reverse().find(f => f.week < gameState.currentWeek && !!f.result), [gameState.currentWeek, allPlayerFixtures]);
    const last5Fixtures = useMemo(() => allPlayerFixtures.filter(f => f.week < gameState.currentWeek && !!f.result).slice(-5), [gameState.currentWeek, allPlayerFixtures]);
    
    const getPlayerById = useCallback((id: number): Player | undefined => {
        for (const league of Object.values(gameState.leagues) as League[]) {
            for (const team of league.teams) {
                const player = team.squad.find(p => p.id === id);
                if (player) return player;
            }
        }
        return undefined;
    }, [gameState.leagues]);

    return (
        <>
            <WeeklyPunditPanel gameState={gameState} pushView={pushView} />

            <div className="dashboard-overview-grid">
                <NextMatchPanel
                    nextFixture={nextFixture}
                    gameState={gameState}
                    pushView={pushView}
                    onFixtureClick={setSelectedFixture}
                />
                <LastResultPanel
                    lastFixture={lastFixture}
                    gameState={gameState}
                    getPlayerById={getPlayerById}
                    pushView={pushView}
                    onFixtureClick={setSelectedFixture}
                />
            </div>

            <TeamOverviewPanel gameState={gameState} pushView={pushView} last5Fixtures={last5Fixtures} onFixtureClick={setSelectedFixture} />

            <TeamStatusPanel
                gameState={gameState}
                pushView={pushView}
            />

            {selectedFixture && <MatchDetailsModal 
                isOpen={!!selectedFixture} 
                onClose={() => setSelectedFixture(null)} 
                fixture={selectedFixture} 
                getPlayerById={getPlayerById}
                gameState={gameState}
                pushView={pushView}
                isAiPunditEnabled={isAiPunditEnabled}
                showRatingsOnMatchReport={showRatingsOnMatchReport}
                onCacheAiMatchReport={onCacheAiMatchReport}
            />}
            {playerToTalkTo && (
                <PlayerConversationModal
                    player={playerToTalkTo}
                    onClose={() => setPlayerToTalkTo(null)}
                    onAction={onPlayerConversation}
                    onOfferFinancialIncentive={onOfferFinancialIncentive}
                    gameState={gameState}
                    pushView={pushView}
                    isAiPunditEnabled={isAiPunditEnabled}
                    canTalk={gameState.currentWeek > (gameState.managerTalkCooldownEnd?.week || 0) || gameState.season > (gameState.managerTalkCooldownEnd?.season || 0)}
                    cooldown={gameState.managerTalkCooldownEnd}
                />
            )}
        </>
    );
};